import { Alert, AnnouncementBanner, Clickable, P } from '@ahmdigital/ui';
import { filter, first, flow, map } from 'lodash/fp';
import React from 'react';

import {
  AlertSection,
  AlertWrapper,
  AnnouncementWrapper,
  Heading,
  OptionColumn,
  OptionsContainer,
  OptionsInnerContainer,
  OptionsRow,
  OptionsSection,
} from './styles';
import { FRAGMENTS } from '../../../../cms/constants';
import { SLUG } from '../../../../models/table-of-contents/constants';
import { URLS } from '../../../../models/url/constants';
import ContactOption from '../contact-option';
import GetACallBack from './get-a-call-back';
import LiveChat from '../live-chat';
import useTableOfContents from '../../../../cms/utils/use-table-of-contents';
import type { ContactProps } from '../types';

export const optionConstantIds = {
  callUs: 'callUs',
  currentMemberChatBox: 'currentMemberChatBox',
  getACallBack: 'getACallBack',
  lookingToJoinChatBox: 'lookingToJoinChatBox',
  memberLogIn: 'memberLogIn',
};

type CallUsProps = {
  data: ContactProps;
};
const CallUs = ({ data }: CallUsProps): JSX.Element => (
  <ContactOption buttonText={data.buttonText} buttonUrl={data.buttonUrl} subText={data.subText} title={data.title} />
);

type ChatBoxProps = {
  data: {
    offline: ContactProps;
    online: ContactProps;
  };
};
const CurrentMemberChatBox = ({ data }: ChatBoxProps): JSX.Element => <LiveChat chatType="currentMember" {...data} />;
const LookingToJoinChatBox = ({ data }: ChatBoxProps): JSX.Element => <LiveChat chatType="lookingToJoin" {...data} />;

const MemberLogIn = (): JSX.Element => (
  <ContactOption
    buttonText="Member log in"
    buttonUrl={URLS.MEMBERS.HOME}
    isHighlighted
    subText={[
      'Check annual limits, claim for extras, update your details, find information about your policy and more.',
    ]}
    title="Manage your cover online"
  />
);

// @ts-expect-error - Automatic, Please fix when editing this file
const getComponentHandler = ({ contactOptionData, contactOptionId }) => {
  switch (contactOptionId) {
    case optionConstantIds.callUs:
      return <CallUs data={contactOptionData} />;
    case optionConstantIds.currentMemberChatBox:
      return <CurrentMemberChatBox data={contactOptionData} />;
    case optionConstantIds.getACallBack:
      return <GetACallBack />;
    case optionConstantIds.lookingToJoinChatBox:
      return <LookingToJoinChatBox data={contactOptionData} />;
    case optionConstantIds.memberLogIn:
      return <MemberLogIn />;
    default:
      return null;
  }
};

const AlertMessage = (): JSX.Element => (
  <Alert heading="There are long waits to chat to us right now due to high call volumes" level="info">
    <P>
      Skip the call queue and get it done in a flash online! You can always{' '}
      <Clickable to={URLS.MEMBERS.HOME}>log in</Clickable> to check your limits, claim for extras, update payment
      details, update your income tier or find information about your policy. We may also answered your question in our
      handing <Clickable to={URLS.HELP.HOME}>help section</Clickable>.
    </P>
  </Alert>
);

type ContactOptionsProps = Readonly<{
  id?: string;
  options: ReadonlyArray<{
    contactOptionId: string;
  }>;
  showAlertMessage?: boolean;
  title?: string;
  showContactUsBanner?: boolean;
}>;

const ContactOptions: React.FC<ContactOptionsProps> = ({
  options,
  showAlertMessage = false,
  title = 'Didn’t find what you’re after?',
  id,
  showContactUsBanner = false,
}) => {
  const { items: alertData } = useTableOfContents({
    fragment: FRAGMENTS.ANNOUNCEMENTS,
    skip: false,
    slug: SLUG.CONTACT_MEMBER_BANNER,
  });

  const alertItem = flow(map('item'), filter({ _type: 'alert' }), first)(alertData) as
    | { descriptionRaw: string }
    | undefined;

  return (
    <>
      {showAlertMessage && (
        <AlertSection variant={['regular', 'marginTopSpacer3']}>
          <AlertWrapper>
            <AlertMessage />
          </AlertWrapper>
        </AlertSection>
      )}

      <OptionsSection>
        <OptionsContainer>
          {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call. */}
          <Heading id={id} tabIndex="0" variant={['center', 'marginBottomSpacer10']}>
            {title}
          </Heading>
          {alertItem && showContactUsBanner && (
            <AnnouncementWrapper>
              <AnnouncementBanner message={alertItem?.descriptionRaw} backgroundColor="" />
            </AnnouncementWrapper>
          )}
          <OptionsInnerContainer>
            <OptionsRow>
              {map(
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'contactOptionData' does not exist on type '{ contactOptionId: string; }'.
                ({ contactOptionId, contactOptionData }) => (
                  <OptionColumn df={4} sm={12} key={contactOptionId}>
                    {getComponentHandler({ contactOptionData, contactOptionId })}
                  </OptionColumn>
                ),
                options,
              )}
            </OptionsRow>
            <P variant={['marginTopSpacer12', 'center']}>
              Submit feedback:&nbsp;
              <Clickable to={URLS.HELP.COMPLAINTS}>Find out more</Clickable>
            </P>
          </OptionsInnerContainer>
        </OptionsContainer>
      </OptionsSection>
    </>
  );
};
export default ContactOptions;
